import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Cart context — supports multiple products (Collezione Privata).
 * Atelier configurations remain a single-item "buyNow" flow because each
 * configuration requires a personal quote from the atelier.
 *
 * Item shape:
 *   { type: 'product', reference_id, name, price, quantity, image, snapshot }
 */
const CartCtx = createContext(null);

const STORAGE_KEY = 'harizi_cart_v1';

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = (item) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.reference_id === item.reference_id && p.type === item.type);
      if (existing) {
        return prev.map((p) =>
          p.reference_id === item.reference_id && p.type === item.type
            ? { ...p, quantity: p.quantity + (item.quantity || 1) }
            : p,
        );
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
    setIsOpen(true);
  };

  const removeItem = (reference_id, type = 'product') => {
    setItems((prev) => prev.filter((p) => !(p.reference_id === reference_id && p.type === type)));
  };

  const setQuantity = (reference_id, qty, type = 'product') => {
    if (qty <= 0) {
      removeItem(reference_id, type);
      return;
    }
    setItems((prev) =>
      prev.map((p) =>
        p.reference_id === reference_id && p.type === type ? { ...p, quantity: qty } : p,
      ),
    );
  };

  const clear = () => setItems([]);

  const { count, subtotal } = useMemo(
    () => ({
      count: items.reduce((s, it) => s + (it.quantity || 1), 0),
      subtotal: items.reduce((s, it) => s + (it.price || 0) * (it.quantity || 1), 0),
    }),
    [items],
  );

  const value = {
    items,
    addItem,
    removeItem,
    setQuantity,
    clear,
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    count,
    subtotal,
  };

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
